import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Grid, Button, TextField } from '@mui/material';

export default function ApproveAdditionalCostsDialog({
    open,
    handleClose,
    handleConfirmApproval,
    contactName,
    signature,
    date,
    amount,
    costDescription,
    isDiscount,
    darkTheme,
    gross
}) {
    const [amountInput, setAmountInput] = useState('');
    const [comment, setComment] = useState('');
    const [commentError, setCommentError] = useState(false);

    // Resetuj kwotę przy każdym otwarciu dialogu
    useEffect(() => {
        if (open) {
            setAmountInput(amount || '');
            setComment('');
            setCommentError(false);
        }
    }, [open, amount]);

    const handleConfirmClick = () => {
        if (!isDiscount) {
            const parsedInput = parseFloat(amountInput) || 0;
            const parsedOriginal = parseFloat(amount) || 0;
            if (parsedInput !== parsedOriginal && !comment.trim()) {
                setCommentError(true);
                return;
            }
        }
        handleConfirmApproval(amountInput, true, comment);
    };

    // Liczenie brutto w czasie rzeczywistym
    const amountBrutto = amountInput ? (parseFloat(amountInput) * 1.23).toFixed(2) : '';

    return (
        <Dialog open={open} onClose={handleClose} aria-labelledby="confirm-dialog-title">
            <DialogTitle id="confirm-dialog-title" sx={{ backgroundColor: darkTheme ? '#0f0f0f' : '#f5f5f5', fontWeight: 'bold', mb: 2 }}>
                Zatwierdzanie dodatkowych kosztów
            </DialogTitle>
            <DialogContent>
                <Box mb={3}>
                    <Typography variant="body1" gutterBottom>
                        Zatwierdzam {isDiscount ? "rabat wprowadzony" : "prace dodatkowe wprowadzone"} przez <strong>{contactName}</strong>
                        {isDiscount ? ` na kwotę ${amount} zł.` : ' na kwotę:'}
                    </Typography>
                </Box>
                {!isDiscount && (
                    <Box mb={3}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6}>
                                <TextField
                                    label="Kwota netto (zł)"
                                    type="text" // używamy "text", żeby móc obsłużyć przecinek
                                    value={amountInput}
                                    onChange={(e) => {
                                        let value = e.target.value;

                                        // Zamień przecinek na kropkę
                                        value = value.replace(',', '.');

                                        // Pozwól tylko na liczby i jedną kropkę
                                        if (/^\d*\.?\d*$/.test(value) || value === '') {

                                            // Ogranicz do dwóch miejsc po przecinku
                                            const parts = value.split('.');
                                            if (parts[1]?.length > 2) {
                                                value = parts[0] + '.' + parts[1].substring(0, 2);
                                            }

                                            // Sprawdź, czy kwota nie przekracza domyślnej
                                            if (value !== '' && parseFloat(value) > parseFloat(amount)) {
                                                value = amount.toString();
                                            }

                                            setAmountInput(value);
                                        }
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        inputMode: 'decimal', // pokazuje klawiaturę numeryczną na mobile
                                        pattern: '[0-9]*[.,]?[0-9]*',
                                    }}
                                />
                            </Grid>
                            {gross && (
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            padding: '10px',
                                            height: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {amountInput ? (parseFloat(amountInput) * 1.23).toFixed(2) + ' zł brutto' : ''}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
                <Box mb={3}>
                    <Typography variant="body1" gutterBottom>
                        Opis: <i>"{costDescription}"</i>
                    </Typography>
                </Box>
                {!isDiscount && (parseFloat(amountInput) !== parseFloat(amount)) && (
                    <Box mb={3}>
                        <TextField
                            label="Uzasadnienie (wymagane przy zmianie kwoty)"
                            multiline
                            rows={3}
                            value={comment}
                            onChange={(e) => {
                                setComment(e.target.value);
                                if (e.target.value.trim()) {
                                    setCommentError(false);
                                }
                            }}
                            fullWidth
                            variant="outlined"
                            error={commentError}
                            helperText={commentError ? "Uzasadnienie jest wymagane w przypadku zmiany zatwierdzanej kwoty." : ""}
                        />
                    </Box>
                )}

                <Grid container spacing={2} mt={2}>
                    <Grid item xs={6}>
                        <Typography variant="body1">
                            Data: <strong><i>{date}</i></strong>
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body1">
                            Podpis: <strong><i>{signature}</i></strong>
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ backgroundColor: darkTheme ? '#0f0f0f' : '#f5f5f5', p: 2 }}>
                <Button color="secondary" variant="outlined" onClick={handleClose} sx={{ mr: 1 }}>Anuluj</Button>
                <Button variant="contained" onClick={handleConfirmClick} sx={{ backgroundColor: '#1976d2', color: '#fff', '&:hover': { backgroundColor: '#1565c0' } }}>
                    Zatwierdź
                </Button>
            </DialogActions>
        </Dialog>
    );
}
